import React, { useEffect, useMemo, useRef, useState } from "react";
import { Card, Col, Flex, Row, Table, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { useGetPilgrimQuery } from "@services/piglgrims";
import { useLocalizedText } from "@utils/hooks/useLocalizedText";
import { SelectCountry } from "@Component/Form/SelectCountry/SelectCountry";
import { SelectCachedOptions } from "@/Component/SelectCachedOptions";
import { formatDate } from "@utils/helpers";
import { LogoIcon } from "@assets/icons";
import { useAuthContext } from "@/_helper/Auth";
import { HugeiconsIcon } from "@hugeicons/react";
import { User02Icon } from "@hugeicons/core-free-icons";

const { Title, Text } = Typography;

const PRINT_WINDOW_STYLE = `
  @page { size: A4 portrait; margin: 10mm; }
  html, body { background: #ffffff; }
  body {
    margin: 0;
    color: #212529;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
`;

const HIDDEN_ROOT_STYLE = {
  position: "absolute",
  insetInlineStart: "-200vw",
  top: 0,
  width: "210mm",
  padding: "8px",
  zIndex: 11,
  pointerEvents: "none",
  opacity: 0,
};

const FETCHING_CURSOR_OVERLAY_STYLE = {
  position: "fixed",
  inset: 0,
  cursor: "wait",
  zIndex: 9999,
  background: "transparent",
};

const SHEET_STYLE = {
  // width: "190mm",
  margin: "0 auto",
  // padding: "8px 0",
  color: "#1f2937",
  background: "#ffffff",
  fontSize: 12,
};

const CARD_STYLE = {
  borderRadius: 18,
  borderColor: "#e5e7eb",
  boxShadow: "none",
};

const CARD_BODY_STYLE = {
  padding: "1rem 1.1rem",
};

const LABEL_STYLE = {
  color: "rgb(91 83 60)",
  fontSize: 11,
  marginBottom: 2,
  display: "block",
};

const VALUE_STYLE = {
  color: "#111827",
  fontWeight: 600,
  wordBreak: "break-word",
  display: "block",
};

const waitForImages = (doc) =>
  Promise.all(
    Array.from(doc.images).map((img) => {
      if (img.complete) return Promise.resolve();
      return new Promise((resolve) => {
        img.onload = resolve;
        img.onerror = resolve;
      });
    }),
  );

const printNode = async ({ node, title, lang, dir }) => {
  if (!node) return;

  const printWindow = window.open("", "_blank", "width=1200,height=900");
  if (!printWindow) return;

  const styles = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
    .map((styleNode) => styleNode.outerHTML)
    .join("\n");

  printWindow.document.open();
  printWindow.document.write(`
    <!doctype html>
    <html lang="${lang}" dir="${dir}">
      <head>
        <meta charset="utf-8" />
        <title>${title}</title>
        ${styles}
        <style>${PRINT_WINDOW_STYLE}</style>
      </head>
      <body>${node.outerHTML}</body>
    </html>
  `);
  printWindow.document.close();

  await waitForImages(printWindow.document);

  window.setTimeout(() => {
    printWindow.focus();
    printWindow.print();
    window.setTimeout(() => {
      printWindow.close();
    }, 300);
  }, 150);
};

const formatPrintDate = (value, language) => (value ? formatDate(value, language, "DD MMMM YYYY") : "-");

const formatPrintDateTime = (value, language) => (value ? formatDate(value, language, "DD MMMM YYYY HH:mm") : "-");

const getTextValue = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return value;
};

const getBooleanText = (value, t) => {
  if (value === true) return t("YES");
  if (value === false) return t("NO");
  return "-";
};

const getEntityTypeText = (value, t) => {
  if (!value) return "-";
  if (value === "internal" || value === 1 || value === "1") return t("INTERNAL");
  if (value === "external" || value === 2 || value === "2") return t("EXTERNAL");
  return value;
};

const PrintItem = ({ label, value, renderValue }) => (
  <Flex wrap gap={2}>
    <Text style={LABEL_STYLE}>{label}:</Text>
    <Text style={VALUE_STYLE}>{renderValue ? renderValue() : getTextValue(value)}</Text>
  </Flex>
);

const PrintSection = ({ title, children }) => (
  <Card styles={{ body: CARD_BODY_STYLE }} style={CARD_STYLE} className="border">
    <div className="border-bottom pb-2 mb-3">
      <Title level={4} style={{ margin: 0, fontSize: 16 }}>
        {title}
      </Title>
    </div>
    {children}
  </Card>
);
const UserImageFallback = ({ ...props }) => {
  const [isError, setIsError] = useState(false);
  if (isError || !props.src) {
    return <HugeiconsIcon icon={User02Icon} />;
  }

  return <img src={props.src} alt="" {...props} onError={() => setIsError(true)} />;
};

export const PilgrimPrintSheet = ({ pilgrim, pilgrimId, printRequestKey, onPrinted }) => {
  const { user } = useAuthContext();
  console.log("user", user);
  const { t, i18n } = useTranslation();
  const { getLocalizedText } = useLocalizedText();
  const sheetRef = useRef(null);
  const lastPrintedKeyRef = useRef(null);

  const { data: pilgrimRes, isFetching } = useGetPilgrimQuery(pilgrimId, {
    enabled: Boolean(pilgrimId) && !pilgrim,
  });

  const isDataFetching = Boolean(pilgrimId) && !pilgrim && isFetching;

  const resolvedPilgrim = pilgrim ?? pilgrimRes?.data?.data;
  const language = i18n.language;
  const isArabic = language === "ae" || language === "ar";
  const dir = isArabic ? "rtl" : "ltr";

  const printTitle = useMemo(() => {
    const name = getLocalizedText(
      resolvedPilgrim?.personal_profile?.name_ar,
      resolvedPilgrim?.personal_profile?.name_en,
    );
    return [t("PILGRIM_DETAILS"), name].filter(Boolean).join(" - ");
  }, [getLocalizedText, resolvedPilgrim?.personal_profile?.name_ar, resolvedPilgrim?.personal_profile?.name_en, t]);

  useEffect(() => {
    if (!printRequestKey || !resolvedPilgrim || isFetching || !sheetRef.current) {
      return;
    }

    if (lastPrintedKeyRef.current === printRequestKey) {
      return;
    }

    lastPrintedKeyRef.current = printRequestKey;

    window.requestAnimationFrame(async () => {
      await printNode({
        node: sheetRef.current,
        title: printTitle,
        lang: language,
        dir,
      });
      onPrinted?.();
    });
  }, [dir, isFetching, language, onPrinted, printRequestKey, printTitle, resolvedPilgrim]);

  const profile = resolvedPilgrim?.personal_profile;
  const detailed = resolvedPilgrim?.detailed_information;
  const makkah = resolvedPilgrim?.makkah_data;
  const medina = resolvedPilgrim?.medina_data;
  const arrival = resolvedPilgrim?.arrival_departure?.arrival;
  const departure = resolvedPilgrim?.arrival_departure?.departure;
  const nusukCard = resolvedPilgrim?.nusuk_card;
  const passportHandover = resolvedPilgrim?.passport_handover;
  const hasPassportReceipt = resolvedPilgrim?.has_passport_receipt;
  const incidents = detailed?.incident_reports ?? [];
  const name = getLocalizedText(profile?.name_ar, profile?.name_en) || "-";
  const gender = getLocalizedText(profile?.gender?.ar, profile?.gender?.en);
  const nationalityId = profile?.nationality_id;
  const pilgrimStatus = detailed?.pilgrim_status;
  const profileImageUrl = profile?.profile_image_url;

  const incidentsColumns = [
    { title: t("REPORT_NUMBER"), dataIndex: "incident_number", key: "incident_number" },
    {
      title: t("DATE"),
      dataIndex: "reported_at",
      key: "reported_at",
      render: (value) => formatPrintDateTime(value, language),
    },
    {
      title: t("SERVICE_CENTER"),
      key: "service_center",
      render: (_, incident) => getTextValue(incident?.service_center?.name),
    },
    {
      title: t("LOCATION"),
      dataIndex: "location",
      key: "location",
      render: (value) => getTextValue(value),
    },
    {
      title: t("STATUS"),
      dataIndex: "status",
      key: "status",
      render: (value) => getTextValue(value),
    },
    {
      title: t("CASE_TYPE"),
      key: "case_type",
      render: (_, incident) => getLocalizedText(incident?.case_type?.name_ar, incident?.case_type?.name_en) || "-",
    },
  ];

  return (
    <>
      {isDataFetching && <div aria-hidden="true" style={FETCHING_CURSOR_OVERLAY_STYLE} />}
      <div aria-hidden="true" style={HIDDEN_ROOT_STYLE}>
        <div ref={sheetRef} className="pilgrim-print-sheet" dir={dir} lang={language} style={SHEET_STYLE}>
          {/* Header */}
          <Row align="flex-start" justify="space-between" gap={12} className="mb-4">
            <Col>
              <Flex vertical align="center">
                <LogoIcon style={{ width: 50 }} />
                <Title level={4} style={{ margin: 0 }} className="sst-arabic">
                  {t("BRAND_NAME")}
                </Title>
                <Flex align="center" className="w-100" gap={3}>
                  <div style={{ height: "1px", backgroundColor: "#000", flex: 1 }}></div>
                  <p style={{ margin: 0, fontSize: "9px" }}>{t("FOR_PILGRIMAGE_SERVICES")}</p>
                </Flex>
              </Flex>
            </Col>
            <Col>
              <Flex align="end" style={{ height: "100%" }}>
                <Title level={3} style={{ margin: 0, fontSize: 20 }}>
                  {t("PILGRIM_REPORT")}
                </Title>
              </Flex>
            </Col>
            <Col>
              <Row gutter={[50, 12]} justify="space-between">
                <Col>
                  <div className="fw-semibold">{t("DATE")}:</div>
                </Col>
                <Col>
                  <div>{formatPrintDateTime(new Date(), language)}</div>
                </Col>
              </Row>
              <Row gutter={[12, 12]} justify="space-between">
                <Col>
                  <div className="fw-semibold">{t("PRINTED_BY")}:</div>
                </Col>
                <Col>
                  <div>{getLocalizedText(user?.name_ar, user?.name_en)}</div>
                </Col>
              </Row>
              <Row gutter={[12, 12]} justify="space-between">
                <Col>
                  <div className="fw-semibold">{t("THE_PAGE")}:</div>
                </Col>
                <Col>
                  <div>1</div>
                </Col>
              </Row>
            </Col>
          </Row>

          {/* Body */}
          <Row gutter={[12, 12]} align="stretch">
            <Col span={10}>
              <Card styles={{ body: CARD_BODY_STYLE }} style={{ ...CARD_STYLE, height: "100%" }} className="border">
                <Flex align="flex-start" gap={16} className="mb-3">
                  <Flex
                    align="center"
                    justify="center"
                    style={{
                      width: "50%",
                      height: 140,
                      borderRadius: 12,
                      border: "1px solid #d1d5db",
                      background: "#f3f4f6",
                      color: "#9ca3af",
                      fontSize: 9,
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    <UserImageFallback
                      src={profileImageUrl}
                      alt={name}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  </Flex>
                  <div className="flex-grow-1">
                    <Title level={6} className="mb-3" style={{ marginTop: 0, fontSize: 16 }}>
                      {name}
                    </Title>
                    <Row gutter={[0, 12]}>
                      <Col span={24}>
                        <PrintItem label={t("PASSPORT_NUMBER")} value={profile?.passport_number} />
                      </Col>
                      <Col span={24}>
                        <PrintItem
                          label={t("NATIONALITY")}
                          renderValue={() => <SelectCountry.Display value={nationalityId} />}
                        />
                      </Col>
                      <Col span={24}>
                        <PrintItem label={t("AGE")} value={profile?.age} />
                      </Col>
                      <Col span={24}>
                        <PrintItem label={t("GENDER")} value={gender} />
                      </Col>
                      <Col span={24}>
                        <PrintItem
                          label={t("COUNTRY")}
                          renderValue={() => <SelectCountry.Display value={profile?.residence_country_id} />}
                        />
                      </Col>
                    </Row>
                  </div>
                </Flex>

                <div className="border-top pt-3">
                  <Row gutter={[12, 12]}>
                    <Col span={12}>
                      <PrintItem label={t("PILGRIM_NUMBER")} value={profile?.hajj_id} />
                    </Col>
                    <Col span={12}>
                      <PrintItem label={t("NUSUK_CARD_NUMBER")} value={nusukCard?.card_number} />
                    </Col>
                    <Col span={12}>
                      <PrintItem label={t("PILGRIM_TYPE")} value={profile?.haj_type} />
                    </Col>
                    <Col span={12}>
                      <PrintItem label={t("ID_NUMBER_LABEL")} value={profile?.identifier_number} />
                    </Col>
                    <Col span={12}>
                      <PrintItem label={t("VISA_NUMBER")} value={detailed?.visa_number} />
                    </Col>
                    <Col span={12}>
                      <PrintItem label={t("VISA_SOURCE")} value={detailed?.visa_source} />
                    </Col>
                    <Col span={12}>
                      <PrintItem label={t("ORGANIZER")} value={detailed?.organizer_name} />
                    </Col>
                    <Col span={12}>
                      <PrintItem label={t("ENTRY_PORT")} value={arrival?.entry_port} />
                    </Col>
                    <Col span={12}>
                      <PrintItem
                        label={t("SERVICE_CENTER")}
                        renderValue={() => (
                          <SelectCachedOptions.Display
                            value={makkah?.service_center_id}
                            campareBy="id"
                            optionKey="service_centers"
                            render={(center) => getLocalizedText(center?.name_ar, center?.name_en) || "-"}
                          />
                        )}
                      />
                    </Col>
                    <Col span={12}>
                      <PrintItem label={t("CAMPAIGN_NUMBER")} value={makkah?.campaign_number} />
                    </Col>
                    <Col span={12}>
                      <PrintItem label={t("GROUP_NUMBER")} value={makkah?.group_number} />
                    </Col>
                    <Col span={12}>
                      <PrintItem label={t("BAG_NUMBER")} value={detailed?.bag_number_medina} />
                    </Col>
                    <Col span={12}>
                      <PrintItem label={t("ARRIVAL_DATE")} value={formatPrintDate(makkah?.arrival_date, language)} />
                    </Col>
                    <Col span={12}>
                      <PrintItem
                        label={t("STATUS")}
                        renderValue={() => getLocalizedText(pilgrimStatus?.label_ar, pilgrimStatus?.label_en) || "-"}
                      />
                    </Col>
                  </Row>
                </div>
              </Card>
            </Col>

            <Col span={14}>
              <Flex vertical gap={12}>
                <PrintSection title={t("ACCOMMODATION_DATA")}>
                  <Row gutter={[12, 12]}>
                    <Col span={12}>
                      <PrintItem label={`${t("MAKKAH_CONTRACT_NUMBER")}`} value={makkah?.contract_id} />
                    </Col>
                    <Col span={12}>
                      <PrintItem label={`${t("MADINA_CONTRACT_NUMBER")}`} value={medina?.contract_id} />
                    </Col>
                    <Col span={12}>
                      <PrintItem
                        label={`${t("MAKKAH_BUILDING_NAME")}`}
                        value={getLocalizedText(makkah?.building_name_ar, makkah?.building_name_en)}
                      />
                    </Col>
                    <Col span={12}>
                      <PrintItem
                        label={`${t("MADINA_BUILDING_NAME")}`}
                        value={getLocalizedText(medina?.building_name_ar, medina?.building_name_en)}
                      />
                    </Col>
                    <Col span={12}>
                      <PrintItem label={`${t("ADDRESS_IN_MAKKAH")}`} value={makkah?.address} />
                    </Col>
                    <Col span={12}>
                      <PrintItem label={`${t("ADDRESS_IN_MADINA")}`} value={medina?.address} />
                    </Col>
                  </Row>
                </PrintSection>

                <PrintSection title={t("ARRIVAL_DATA")}>
                  <Row gutter={[12, 12]}>
                    <Col span={12}>
                      <PrintItem
                        label={t("ARRIVAL_DATE")}
                        value={formatPrintDate(arrival?.actual_arrival_date, language)}
                      />
                    </Col>
                    <Col span={12}>
                      <PrintItem
                        label={t("EXPECTED_ARRIVAL_DATE")}
                        value={formatPrintDate(arrival?.expected_arrival_date, language)}
                      />
                    </Col>
                    <Col span={12}>
                      <PrintItem label={t("FLIGHT_NUMBER")} value={arrival?.flight_number} />
                    </Col>
                    <Col span={12}>
                      <PrintItem label={t("DIRECTION")} value={arrival?.direction} />
                    </Col>
                    <Col span={12}>
                      <PrintItem
                        label={t("MAKKAH_ARRIVAL_DATE")}
                        value={formatPrintDate(makkah?.arrival_date, language)}
                      />
                    </Col>
                    <Col span={12}>
                      <PrintItem
                        label={t("MADINA_ARRIVAL_DATE")}
                        value={formatPrintDate(medina?.arrival_date, language)}
                      />
                    </Col>
                  </Row>
                </PrintSection>

                <PrintSection title={t("DEPARTURE_DATA")}>
                  <Row gutter={[12, 12]}>
                    <Col span={12}>
                      <PrintItem label={t("EXIT_PORT")} value={departure?.exit_port} />
                    </Col>
                    <Col span={12}>
                      <PrintItem
                        label={t("ACTUAL_DEPARTURE_DATE")}
                        value={formatPrintDate(departure?.actual_departure_date, language)}
                      />
                    </Col>
                    <Col span={12}>
                      <PrintItem
                        label={t("EXPECTED_DEPARTURE_DATE")}
                        value={formatPrintDate(departure?.expected_departure_date, language)}
                      />
                    </Col>
                    <Col span={12}>
                      <PrintItem label={t("DEPARTURE_DIRECTION")} value={departure?.direction_upon_departure} />
                    </Col>
                    <Col span={12}>
                      <PrintItem
                        label={t("NUSUK_DEPARTURE_REPORT_NUMBER")}
                        value={departure?.departure_report_number}
                      />
                    </Col>
                  </Row>
                </PrintSection>

                {/* <PrintSection title={t("PASSPORT_RECEIPT_DOCUMENT")}>
                  <Row gutter={[12, 12]}>
                    <Col span={12}>
                      <PrintItem
                        label={t("HAS_PASSPORT_RECEIPT")}
                        value={getBooleanText(hasPassportReceipt, t)}
                      />
                    </Col>
                    <Col span={12}>
                      <PrintItem
                        label={t("PASSPORT_PHYSICALLY_DELIVERED")}
                        value={getBooleanText(passportHandover?.passport_physically_delivered, t)}
                      />
                    </Col>
                    <Col span={12}>
                      <PrintItem
                        label={t("PASSPORT_RECEIPT_PRINTED")}
                        value={getBooleanText(passportHandover?.passport_receipt_printed, t)}
                      />
                    </Col>
                    <Col span={12}>
                      <PrintItem label={t("RECEIPTS_COUNT")} value={passportHandover?.receipts_count} />
                    </Col>
                    <Col span={12}>
                      <PrintItem label={t("ENTITY_NAME")} value={passportHandover?.entity_name} />
                    </Col>
                    <Col span={12}>
                      <PrintItem label={t("RECEIVER_NAME")} value={passportHandover?.receiver_name} />
                    </Col>
                    <Col span={12}>
                      <PrintItem
                        label={t("NUSUK_RECEIPT_DATE")}
                        value={hasPassportReceipt ? formatPrintDateTime(passportHandover?.receipt_at, language) : ""}
                      />
                    </Col>
                    <Col span={12}>
                      <PrintItem label={t("ENTITY_TYPE")} value={getEntityTypeText(passportHandover?.entity_type, t)} />
                    </Col>
                    <Col span={12}>
                      <PrintItem label={t("EXTERNAL_ENTITY_NAME")} value={passportHandover?.external_entity_name} />
                    </Col>
                    <Col span={12}>
                      <PrintItem
                        label={t("INTERNAL_JOB_SECTOR_ID")}
                        renderValue={() => (
                          <SelectCachedOptions.Display
                            value={passportHandover?.internal_job_sector_id}
                            campareBy="id"
                            optionKey="job_sectors"
                            render={(sector) =>
                              getLocalizedText(sector?.name_ar, sector?.name_en) ||
                              sector?.name ||
                              "-"
                            }
                          />
                        )}
                      />
                    </Col>
                    <Col span={12}>
                      <PrintItem label={t("RECEIPT_REASON")} value={passportHandover?.receipt_reason} />
                    </Col>
                    <Col span={12}>
                      <PrintItem label={t("CREATED_BY")} value={passportHandover?.created_by_name} />
                    </Col>
                    <Col span={12}>
                      <PrintItem label={t("PRINTED_BY")} value={passportHandover?.printed_by_name} />
                    </Col>
                    <Col span={12}>
                      <PrintItem
                        label={t("LAST_PRINTED_AT")}
                        value={formatPrintDateTime(passportHandover?.last_printed_at, language)}
                      />
                    </Col>
                  </Row>
                </PrintSection> */}

                <PrintSection title={t("REPORTS")}>
                  {incidents.length === 0 ? (
                    <div className="text-muted">{t("NO_DATA")}</div>
                  ) : (
                    <Table
                      columns={incidentsColumns}
                      dataSource={incidents}
                      rowKey={(incident, index) => incident?.incident_number ?? index}
                      pagination={false}
                      size="small"
                      bordered
                    />
                  )}
                </PrintSection>
              </Flex>
            </Col>
          </Row>
        </div>
      </div>
    </>
  );
};

export default PilgrimPrintSheet;
